import { useEffect, useState } from "react";
import { formatPairingCode, normalizePairingCode } from "@racler/shared";
import { Header } from "../components/Header.tsx";
import { api, errorText, type User } from "../lib/api.ts";
import { navigate } from "../lib/router.ts";
import {
  clearPendingPairingCode,
  clearUrlHash,
  extractPairingCode,
  getPendingPairingCode,
} from "../lib/pairingUrl.ts";

type Pending = Awaited<ReturnType<typeof api.lookupPairing>>;

/** Two steps: enter the code shown by the app or CLI, then explicitly approve name + key fingerprint. */
export function PairPage({ user, onLogout }: { user: User; onLogout: () => void }) {
  const [code, setCode] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const fromHash = typeof window !== "undefined" ? extractPairingCode(window.location.hash) : null;
    const raw = fromHash ?? getPendingPairingCode();
    clearUrlHash();
    clearPendingPairingCode();

    if (!raw) return;

    const normalized = normalizePairingCode(raw);
    if (!normalized) {
      setError("Invalid pairing code.");
      return;
    }

    const formatted = formatPairingCode(normalized);
    setCode(formatted);
    void run(async () => {
      const result = await api.lookupPairing(normalized);
      setPending(result);
    });
  }, []);

  return (
    <div className="page">
      <Header user={user} onLogout={onLogout} />
      <main className="center">
        <div className="card">
          <h1>Pair a machine</h1>
          {!pending ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const normalized = normalizePairingCode(code);
                if (!normalized) {
                  setError("Please enter a valid 8-character pairing code.");
                  return;
                }
                void run(async () => setPending(await api.lookupPairing(normalized)));
              }}
            >
              <p className="muted">
                Run the racler.dev desktop app, or run{" "}
                <code>racler agent pair --server {location.origin}</code> in your terminal, and enter the code
                it shows.
              </p>
              <input
                className="code-input"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                maxLength={9}
                data-testid="pair-code"
              />
              <button
                className="primary"
                disabled={busy || code.replace(/[\s-]/g, "").length !== 8}
                data-testid="pair-lookup"
              >
                Continue
              </button>
            </form>
          ) : (
            <div>
              <p>
                Pair <strong>“{pending.name}”</strong> ({pending.platform}) with your account?
              </p>
              <p className="muted small">
                Pairing code: <code>{formatPairingCode(normalizePairingCode(code) ?? code)}</code>
                <br />
                Key fingerprint: <code>{pending.fingerprint}</code> — it must match the one shown in the
                racler.dev app or CLI.
                <br />
                Approving allows this machine to be controlled from your account.
              </p>
              <div className="row">
                <button
                  className="primary"
                  disabled={busy}
                  data-testid="pair-approve"
                  onClick={() =>
                    void run(async () => {
                      const normalized = normalizePairingCode(code) ?? code;
                      const { machine } = await api.approvePairing(normalized);
                      navigate(`/m/${machine.id}`);
                    })
                  }
                >
                  Approve
                </button>
                <button onClick={() => setPending(null)}>Cancel</button>
              </div>
            </div>
          )}
          {error && <p className="error">{error}</p>}
        </div>
      </main>
    </div>
  );
}
